import { Link, useParams } from "react-router";
import { Aviso, Cartao, Chip } from "../componentes/ui";
import { contatoDoModulo, nomeDaEtapaDoModulo } from "../nucleo/andamentoDoModulo";
import { formatarData, useMinhaEsteira } from "../nucleo/implantacoes";
import {
  PERMISSAO_DA_TELA_CONTRATO,
  caminhoDoTermoDeEncerramento,
  useTermosDeEncerramento,
} from "../nucleo/contratoDoCliente";
import { useTrilhas } from "../nucleo/kickoff";
import { podeNaSessao, useSessao } from "../nucleo/sessao";
import { useTitulo } from "../nucleo/titulo";

/** A visão do módulo existe desde o contrato; os dados do Jira entram nela quando nascerem. */
export function ModuloDaImplantacao() {
  const { modulo: chave = "" } = useParams();
  const sessao = useSessao();
  /* A consulta não tem `esteira:ver` (matriz da spec §4): pedir a esteira a ela só colhia um 403
     mostrado como erro. Ela tem `kickoff:ver`, então o que dá para mostrar é o kickoff do módulo. */
  const acessoDeConsulta = !!sessao.data && !podeNaSessao(sessao.data, "esteira:ver");
  const esteira = useMinhaEsteira(!sessao.isPending && !acessoDeConsulta);
  const trilhas = useTrilhas();
  const modulo = esteira.data?.modulos.find((item) => item.chave === chave);
  /* O termo de encerramento só é do módulo repassado, e só abre para quem abre a tela Contrato. */
  const podeVerOTermo = podeNaSessao(sessao.data, PERMISSAO_DA_TELA_CONTRATO);
  const termos = useTermosDeEncerramento(podeVerOTermo && Boolean(modulo?.repassado_em));
  const termoDoModulo = termos.data?.find((termo) => termo.modulo_chave === chave);
  const trilhaDaConsulta = trilhas.data?.trilhas.find((item) => item.chave === chave);
  useTitulo(modulo?.nome ?? trilhaDaConsulta?.nome ?? "Módulo");

  if (sessao.isPending || (!acessoDeConsulta && esteira.isPending) || trilhas.isPending) {
    return <p>Carregando o módulo…</p>;
  }
  if ((!acessoDeConsulta && esteira.isError) || trilhas.isError) {
    return <Aviso tom="erro">Não foi possível carregar este módulo agora.</Aviso>;
  }
  if (acessoDeConsulta) {
    if (!trilhaDaConsulta) {
      return <Aviso tom="atencao">Este módulo não faz parte desta implantação.</Aviso>;
    }
    return (
      <>
        <h1 className="titulo-pagina">{trilhaDaConsulta.nome}</h1>
        <Aviso tom="marca" className="mb-4" data-testid="modulo-fora-do-acesso">
          <b>O seu acesso é de consulta.</b> Ele abre as respostas do kickoff, mas não o andamento
          nem os Recursos Plus deste módulo. Se você precisa acompanhá-los, peça a quem administra
          o acesso da sua empresa.
        </Aviso>
        <Cartao titulo="Kickoff do módulo" data-testid="kickoff-do-modulo">
          <p>{trilhaDaConsulta.progresso.percentual}% das perguntas obrigatórias concluídas.</p>
          <Link className="botao botao--fantasma" to={`/app/formularios/${chave}`}>
            Ver as respostas
          </Link>
        </Cartao>
      </>
    );
  }
  if (!modulo) return <Aviso tom="atencao">Este módulo não faz parte desta implantação.</Aviso>;

  const trilha = trilhas.data?.trilhas.find((item) => item.chave === chave);
  const acompanhamentoLiberado = esteira.data?.acompanhamento_liberado;
  const recursos = (esteira.data?.recursos_plus ?? []).filter(
    (recurso) =>
      modulo.jira_project_id != null && recurso.jira_project_id === modulo.jira_project_id,
  );
  const contato = contatoDoModulo(modulo, esteira.data?.contato ?? null);

  return (
    <>
      <h1 className="titulo-pagina">{modulo.nome}</h1>
      <p className="lead">
        Acompanhe as informações, os Recursos Plus e o andamento deste módulo separadamente.
      </p>

      <div className="grade-de-campos grade-de-campos--duas">
        <Cartao titulo="Etapa do módulo" data-testid="etapa-do-modulo">
          {modulo.repassado_em ? (
            <div data-testid="modulo-repassado">
              <p>
                <b>Concluído e repassado ao atendimento</b> em{" "}
                {formatarData(modulo.repassado_em) ?? "—"}.
              </p>
              <p className="nota-rodape mt-1">
                Para pedir ajuda sobre este módulo, use o menu Chamados.
              </p>
              {termoDoModulo ? (
                <p className="mt-2">
                  <a href={caminhoDoTermoDeEncerramento(termoDoModulo.id)} download data-testid="baixar-termo">
                    Baixar o termo de encerramento (PDF)
                  </a>
                </p>
              ) : null}
            </div>
          ) : acompanhamentoLiberado && modulo.jira_task_key ? (
            <p>{nomeDaEtapaDoModulo(modulo.jira_status_nome) ?? "Aguardando atualização"}.</p>
          ) : acompanhamentoLiberado ? (
            <p>Kickoff concluído. Aguardando a atualização deste módulo.</p>
          ) : (
            <p>O andamento fica disponível após a conclusão do kickoff. A execução também aguarda a reunião.</p>
          )}
        </Cartao>
        <Cartao titulo="Seu contato">
          {contato?.nome ? (
            <p>
              <strong>{contato.nome}</strong><br />
              {contato.email ? (
                <a href={`mailto:${contato.email}`}>{contato.email}</a>
              ) : null}
            </p>
          ) : (
            <p>O contato será informado pela equipe de implantação.</p>
          )}
        </Cartao>
      </div>

      <Cartao titulo="Kickoff do módulo" data-testid="kickoff-do-modulo">
        {trilha ? (
          <>
            <p>{trilha.progresso.percentual}% das perguntas obrigatórias concluídas.</p>
            <Link className="botao botao--fantasma" to={`/app/formularios/${chave}`}>
              {trilha.progresso.concluida ? "Rever respostas" : "Continuar preenchimento"}
            </Link>
          </>
        ) : (
          <p>Este módulo não tem perguntas próprias publicadas.</p>
        )}
      </Cartao>

      <Cartao titulo="Recursos Plus" data-testid="recursos-do-modulo">
        {recursos.length ? (
          <ul className="lista-de-marcas">
            {recursos.map((recurso) => (
              <li className="lista-de-marcas__item" key={recurso.chave}>
                {recurso.nome}{" "}
                <Chip estado="neutro">
                  {acompanhamentoLiberado ? (nomeDaEtapaDoModulo(recurso.jira_status_nome) ??
                    (recurso.jira_issue_key ? "Aguardando atualização" : "Aguardando execução")) : "Aguardando conclusão do kickoff"}
                </Chip>
              </li>
            ))}
          </ul>
        ) : (
          <p>Nenhum Recurso Plus vinculado a este módulo.</p>
        )}
      </Cartao>
    </>
  );
}
